import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getInvoiceByDetails } from '../../../redux/Supplier/action';
import { TableRowsLoader } from '../../../utility/Constant';

/**
 * Styles for common UI elements
 */
const styles = {
    // Dialog header styles
    dialogHeader: {
        height: '64px',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },

    // Dialog content styles
    dialogContent: {
        padding: '24px!important',
        maxHeight: '750px',
        overflow: 'auto',
    },

    // Table header cell styles
    tableHeaderCell: {
        padding: '8px 12px',
        width: '163px',
        height: '40px',
        lineHeight: 'normal',
        fontWeight: 500,
        fontSize: '14px',
        color: '#000',
        // display: "flex",
        // alignItems: "center",
    },

    // Table body cell styles
    tableBodyCell: {
        padding: '0 12px',
        height: '36px !important',
        minHeight: '36px !important',
        maxHeight: '36px !important',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
    },

    // Typography for table cell content
    tableCellTypography: {
        fontSize: 14,
        fontWeight: 400,
        letterSpacing: '0.17px',
        lineHeight: '143%',
    },

    // Message text style
    messageText: {
        fontSize: '16px',
        marginBottom: '20px',
        fontWeight: 400,
        lineHeight: '24px',
        color: '#000',
    },

    // Link icon style
    linkIcon: {
        fontSize: '18px',
        color: '#006CDD',
        cursor: 'pointer',
    },
};

/**
 * PartUsedInvoiceModal - Displays information about customer invoices using a specific part
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.onClose - Handler function for closing the dialog
 * @param {Array} props.invoiceData - Array of invoice data to display
 * @returns {React.Component}
 */
export default function PartUsedInvoiceModal({ open, batchNumber = '', inventoryId = '', supplierInvoiceId = '', supplierId = '', onClose }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [isLoader, setLoader] = useState(false);
    const [invoiceData, setInvoiceData] = useState([]);
    // Sort parameters for table columns
    const [sortParams, setSortParams] = useState({
        key: 'customerName',
        orderBy: 'asc',
    });

    const fetchApi = async () => {
        setLoader(true);
        const resp = await dispatch(
            getInvoiceByDetails({
                batchNumber,
                inventoryId,
                supplierInvoiceId,
                supplierId,
                pagination: true,
                limit: 552,
                populate: 'customer',
            })
        );
        if (resp.data?.length > 0) {
            setInvoiceData(resp?.data);
        } else {
            setInvoiceData([]);
        }
        setLoader(false);
    };

    useEffect(() => {
        if (open) {
            fetchApi();
        }
    }, [open]);

    /**
     * Handles sorting for table columns
     */
    const handleSortRequest = (key) => {
        setSortParams((prev) => ({
            key: key,
            orderBy: prev.key === key && prev.orderBy === 'asc' ? 'desc' : 'asc',
        }));
    };

    /**
     * Sort the invoice data based on current sort parameters
     */
    // const sortedData = [...invoiceData].sort((a, b) => {
    //     const { key, orderBy } = sortParams;

    //     if (orderBy === 'asc') {
    //         return a[key] > b[key] ? 1 : -1;
    //     } else {
    //         return a[key] < b[key] ? 1 : -1;
    //     }
    // });

    /**
     * Renders table header cells with sort functionality
     * @param {string} label - Column label
     * @param {string} sortKey - Key for sorting
     * @returns {React.Component}
     */
    const renderTableHeaderCell = (label, sortKey) => (
        <TableCell align="left" padding="0" sx={styles.tableHeaderCell}>
            {/* <TableSortLabel
          active={sortParams.key === sortKey}
          direction={sortParams.key === sortKey ? sortParams.orderBy : "asc"}
          onClick={() => handleSortRequest(sortKey)}
          IconComponent={ArrowDropDownIcon}
        > */}
            {label}
            {/* </TableSortLabel> */}
        </TableCell>
    );

    /**
     * Renders a standard table body cell with typography
     * @param {string|number} content - Cell content
     * @returns {React.Component}
     */
    const renderTableBodyCell = (content) => (
        <TableCell sx={styles.tableBodyCell} align="left">
            <Typography sx={styles.tableCellTypography}>{content}</Typography>
        </TableCell>
    );

    /**
     * Renders link to invoice cell
     * @param {string} invoiceId - Invoice ID to link to
     * @returns {React.Component}
     */
    const renderLinkCell = (redirectPath) => (
        <TableCell sx={styles.tableBodyCell} align="left">
            <IconButton
                size="small"
                onClick={() => {
                    if (redirectPath) {
                        navigate(redirectPath);
                    }
                }}
            >
                <LaunchIcon sx={styles.linkIcon} />
            </IconButton>
        </TableCell>
    );

    return (
        <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
            {/* Dialog Header */}
            <DialogTitle sx={styles.dialogHeader}>
                <Typography variant="h6">Part is used in customer invoice(s)</Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon style={{ fill: 'black', fontSize: 32 }} />
                </IconButton>
            </DialogTitle>

            {/* Dialog Content */}
            <DialogContent sx={styles.dialogContent}>
                {/* Description text */}
                <Typography sx={styles.messageText}>
                    This part has been used on {invoiceData.length} customer invoice
                    {invoiceData.length !== 1 ? 's' : ''}. In order to to maintain accurate inventory costing, it cannot be deleted.
                </Typography>

                {/* Invoices Table */}
                <TableContainer component={Paper} sx={{ boxShadow: 'none', maxHeight: '225px', overflowY: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ height: '40px', padding: '0' }}>
                                {renderTableHeaderCell('Customer Name', 'customerName')}
                                {renderTableHeaderCell('Invoice #', 'invoiceNumber')}
                                {renderTableHeaderCell('Invoice Date', 'invoiceDate')}
                                {renderTableHeaderCell(' Link to invoice', 'linkToInvoice')}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoader ? (
                                <TableRowsLoader rowsNum={3} cellsNum={4} />
                            ) : invoiceData?.length > 0 ? (
                                invoiceData.map((invoice, index) => (
                                    <TableRow key={index} sx={{ height: '36px' }}>
                                        {renderTableBodyCell(invoice?.customer?.fullName)}
                                        {renderTableBodyCell(invoice?.invoiceNumber)}
                                        {renderTableBodyCell(invoice?.createdAt ? dayjs(invoice.createdAt).format('MMMM DD YYYY') : '-')}
                                        {renderLinkCell(
                                            invoice?.status === 'draft'
                                                ? `/invoice/draft/${invoice?._id}`
                                                : invoice?.isEmailSent
                                                  ? `/invoice/sent/${invoice?._id}`
                                                  : `/invoice/finalized/${invoice?._id}`
                                        )}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography sx={styles.tableCellTypography}>No invoices found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
}
